import { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { ogImage } from '../../lib/ogImage';
import { Instagram, ChevronRight } from 'lucide-react';
import BlogLayout from '../../components/BlogLayout';

const faqSchema = {
  "@context": "https://schema.org",
  "@type": "FAQPage",
  mainEntity: [
    { "@type": "Question", name: "Τι να φάω πριν από το τατουάζ;", acceptedAnswer: { "@type": "Answer", text: "Ένα ισορροπημένο γεύμα με σύνθετους υδατάνθρακες και πρωτεΐνη, όπως κοτόπουλο με ρύζι, ζυμαρικά ή αυγά με ψωμί ολικής. Στόχος είναι σταθερή ενέργεια που θα σε κρατήσει καθ' όλη τη διάρκεια της συνεδρίας." } },
    { "@type": "Question", name: "Μπορώ να κάνω τατουάζ με άδειο στομάχι;", acceptedAnswer: { "@type": "Answer", text: "Καλύτερα όχι. Το άδειο στομάχι ρίχνει τα επίπεδα σακχάρου και μπορεί να σε κάνει να νιώσεις ζάλη, αδυναμία ή ναυτία. Πάντα να τρως ένα καλό γεύμα πριν το ραντεβού σου." } },
    { "@type": "Question", name: "Πόσο νωρίτερα πρέπει να φάω πριν το τατουάζ;", acceptedAnswer: { "@type": "Answer", text: "Ιδανικά μία με δύο ώρες πριν το ραντεβού. Αυτό δίνει στο σώμα χρόνο να αφομοιώσει το φαγητό και να έχει σταθερά επίπεδα ενέργειας κατά τη συνεδρία." } },
    { "@type": "Question", name: "Μπορώ να πιω αλκοόλ πριν το τατουάζ;", acceptedAnswer: { "@type": "Answer", text: "Όχι. Το αλκοόλ αραιώνει το αίμα και αυξάνει την αιμορραγία, κάτι που δυσκολεύει τη δουλειά και επηρεάζει την επούλωση. Απόφυγέ το τουλάχιστον είκοσι τέσσερις ώρες πριν." } },
    { "@type": "Question", name: "Πρέπει να πίνω νερό πριν το τατουάζ;", acceptedAnswer: { "@type": "Answer", text: "Ναι. Η καλή ενυδάτωση κάνει το δέρμα πιο εύκολο να δουλευτεί, βοηθάει το σώμα να αντέξει τη διαδικασία και υποστηρίζει την επούλωση." } },
    { "@type": "Question", name: "Τι να έχω μαζί μου σε μια μεγάλη συνεδρία τατουάζ;", acceptedAnswer: { "@type": "Answer", text: "Νερό, ένα σνακ για τα διαλείμματα όπως μια μπάρα ή ξηρούς καρπούς, κάτι γλυκό για ώρα ανάγκης και άνετα ρούχα που αφήνουν την περιοχή του τατουάζ ελεύθερη." } },
    { "@type": "Question", name: "Επηρεάζει η διατροφή την επούλωση του τατουάζ;", acceptedAnswer: { "@type": "Answer", text: "Ναι. Ένα καλά τραφέν και ενυδατωμένο σώμα διαχειρίζεται καλύτερα τη διαδικασία και υποστηρίζει την επούλωση." } },
  ],
};

export default function WhatToEatBeforeTattooAthens() {
  useEffect(() => {
    const inject = (id: string, s: object) => {
      let tag = document.getElementById(id);
      if (!tag) { tag = document.createElement('script'); tag.id = id; (tag as HTMLScriptElement).type = 'application/ld+json'; document.head.appendChild(tag); }
      tag.textContent = JSON.stringify(s);
    };
    const articleSchema = {
      "@context": "https://schema.org",
      "@type": "BlogPosting",
      "headline": "Τι να φάω πριν από το τατουάζ;",
      "description": "Τι πρέπει να φας πριν το ραντεβού για τατουάζ, τι να αποφύγεις, πόσο νωρίτερα να φας και πώς η σωστή διατροφή κάνει την εμπειρία πιο ομαλή.",
      "image": "https://210tattoo.gr/og-image.png",
      "datePublished": "2026-06-18",
      "dateModified": "2026-06-18",
      "author": { "@type": "Organization", "name": "210 Tattoo Athens", "url": "https://210tattoo.gr" },
      "publisher": { "@id": "https://210tattoo.gr/#tattooparlor" },
      "url": "https://210tattoo.gr/blog/what-to-eat-before-tattoo-athens",
      "inLanguage": "el"
    };
    inject('schema-what-to-eat-before-tattoo-faq', faqSchema);
    inject('schema-what-to-eat-before-tattoo-article', articleSchema);
    return () => {
      document.getElementById('schema-what-to-eat-before-tattoo-faq')?.remove();
      document.getElementById('schema-what-to-eat-before-tattoo-article')?.remove();
    };
  }, []);

  return (
    <>
      <Helmet>
        <title>Τι να φάω πριν από το τατουάζ; | 210 Tattoo Athens</title>
        <meta name="description" content="Τι πρέπει να φας πριν το ραντεβού για τατουάζ, τι να αποφύγεις, πόσο νωρίτερα να φας και πώς η σωστή διατροφή κάνει την εμπειρία πιο ομαλή." />
        <link rel="canonical" href="https://210tattoo.gr/blog/what-to-eat-before-tattoo-athens" />
        <link rel="alternate" hrefLang="el" href="https://210tattoo.gr/blog/what-to-eat-before-tattoo-athens" />
        <link rel="alternate" hrefLang="x-default" href="https://210tattoo.gr/blog/what-to-eat-before-tattoo-athens" />
        <meta property="og:title" content="Τι να φάω πριν από το τατουάζ; | 210 Tattoo Athens" />
        <meta property="og:description" content="Τι να φας και τι να αποφύγεις πριν το ραντεβού σου για τατουάζ." />
        <meta property="og:url" content="https://210tattoo.gr/blog/what-to-eat-before-tattoo-athens" />
        <meta property="og:type" content="article" />
        <meta property="og:image" content={ogImage('what-to-eat-before-tattoo-athens')} />
        <meta property="og:image:width" content="1200" />
        <meta property="og:image:height" content="630" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Τι να φάω πριν από το τατουάζ; | 210 Tattoo Athens" />
        <meta name="twitter:description" content="Οδηγός διατροφής πριν το τατουάζ." />
        <meta name="twitter:image" content={ogImage('what-to-eat-before-tattoo-athens')} />
      </Helmet>

      <BlogLayout>
        {/* Hero */}
        <section className="py-20 px-4 sm:px-6 lg:px-8 relative overflow-hidden">
          <div className="absolute inset-0 bg-gradient-to-br from-pink-950/20 via-black to-black pointer-events-none" />
          <div className="max-w-4xl mx-auto relative z-10">
            <div className="flex items-center gap-2 text-pink-500 text-sm font-medium mb-6">
              <a href="/" className="hover:text-pink-400 transition-colors">Home</a>
              <ChevronRight size={14} />
              <span>Διατροφή Πριν το Τατουάζ</span>
            </div>
            <h1 className="text-5xl md:text-6xl font-bold mb-6 leading-tight">
              <span className="bg-gradient-to-r from-pink-500 via-pink-400 to-pink-300 bg-clip-text text-transparent">Τι να φάω</span>
              <br />
              <span className="text-white">πριν από το τατουάζ;</span>
            </h1>
            <p className="text-gray-500 text-xs mb-4 tracking-widest uppercase">18 Ιουνίου 2026</p>
            <p className="text-xl text-gray-300 mb-10 leading-relaxed max-w-2xl">
              Αυτό που τρως πριν κάτσεις στην καρέκλα επηρεάζει άμεσα πώς θα νιώσεις κατά τη διάρκεια της συνεδρίας. Δες τι να φας, τι να αποφύγεις και πόσο νωρίτερα.
            </p>
            <a href="https://ig.me/m/210tattoo" target="_blank" rel="noopener noreferrer" className="bg-gradient-to-r from-pink-600 to-pink-700 hover:from-pink-700 hover:to-pink-800 px-8 py-4 rounded-lg text-lg font-semibold transition-all duration-300 inline-flex items-center gap-3 shadow-2xl hover:shadow-pink-500/30 transform hover:scale-105">
              <Instagram size={20} />
              Κλείσε Ραντεβού
            </a>
          </div>
        </section>

        {/* Why it matters */}
        <section className="py-16 bg-gray-950">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="text-3xl font-bold text-white mb-6">Γιατί η διατροφή πριν το τατουάζ <span className="text-pink-500">έχει σημασία</span></h2>
            <p className="text-gray-300 text-lg leading-relaxed">
              Όση ώρα δουλεύει ο καλλιτέχνης, το σώμα σου βρίσκεται σε μια κατάσταση ήπιου στρες. Παράγει αδρεναλίνη, καίει ενέργεια και διαχειρίζεται τον πόνο. Όταν δεν έχεις φάει σωστά, τα επίπεδα σακχάρου πέφτουν και μπορεί να νιώσεις ζάλη, αδυναμία ή ναυτία. Ένα καλά τραφέν σώμα διαχειρίζεται τον πόνο καλύτερα και βοηθάει στη μετέπειτα επούλωση.
            </p>
          </div>
        </section>

        {/* What to eat */}
        <section className="py-16 bg-black">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="text-3xl font-bold text-white mb-6">Τι να φας <span className="text-pink-500">πριν το τατουάζ</span></h2>
            <div className="grid sm:grid-cols-2 gap-4">
              {[
                { title: 'Σύνθετοι υδατάνθρακες', desc: 'Ψωμί ολικής, ρύζι, πατάτες, ζυμαρικά. Παρέχουν σταθερή, μακράς διαρκείας ενέργεια χωρίς απότομες διακυμάνσεις στο σάκχαρο.' },
                { title: 'Πρωτεΐνη', desc: 'Κοτόπουλο, ψάρι, αυγά, όσπρια ή γιαούρτι. Σε κρατάει χορτάτο και υποστηρίζει το σώμα στη διαδικασία.' },
                { title: 'Υγιεινά λιπαρά', desc: 'Αβοκάντο, ξηροί καρποί, ελαιόλαδο, ψάρι. Παρέχουν σταθερή ενέργεια και βοηθούν το σώμα να λειτουργεί ομαλά.' },
                { title: 'Φρούτα και λαχανικά', desc: 'Παρέχουν βιταμίνες, μέταλλα και αντιοξειδωτικά που υποστηρίζουν το σώμα και την επούλωση.' },
              ].map((item, i) => (
                <div key={i} className="bg-gray-900 rounded-xl p-6 border border-pink-600/10 hover:border-pink-600/30 transition-colors duration-200">
                  <h3 className="text-white font-semibold mb-1">{item.title}</h3>
                  <p className="text-gray-400 text-sm leading-relaxed">{item.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Example meals */}
        <section className="py-16 bg-gray-950">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="text-3xl font-bold text-white mb-6">Παράδειγμα ιδανικού <span className="text-pink-500">γεύματος πριν το τατουάζ</span></h2>
            <div className="flex flex-wrap gap-3">
              {[
                'Κοτόπουλο με ρύζι και σαλάτα',
                'Ζυμαρικά με κρέας ή κοτόπουλο',
                'Αυγά με ψωμί ολικής και αβοκάντο',
                'Όσπρια, ρύζι και λαχανικά',
                'Γιαούρτι με βρώμη, φρούτα και ξηρούς καρπούς',
              ].map((tag, i) => (
                <span key={i} className="border border-pink-600/20 text-gray-300 px-4 py-2 rounded-lg text-sm">{tag}</span>
              ))}
            </div>
          </div>
        </section>

        {/* What to avoid */}
        <section className="py-16 bg-black">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="text-3xl font-bold text-white mb-6">Τι να αποφύγεις <span className="text-pink-500">πριν το τατουάζ</span></h2>
            <div className="space-y-4">
              {[
                { title: 'Αλκοόλ', desc: 'Αραιώνει το αίμα και αυξάνει την αιμορραγία. Απόφυγέ το τουλάχιστον 24 ώρες πριν. Πολλά στούντιο δεν δουλεύουν σε όποιον έχει πιει.' },
                { title: 'Πολλή καφεΐνη', desc: 'Μπορεί να αραιώσει το αίμα, να αυξήσει την ευαισθησία και να σε κάνει νευρικό.' },
                { title: 'Πολλή ζάχαρη και πρόχειρο φαγητό', desc: 'Δίνει απότομη ενέργεια που πέφτει εξίσου γρήγορα, αφήνοντάς σε με χαμηλό σάκχαρο στη μέση της συνεδρίας.' },
                { title: 'Φάρμακα που αραιώνουν το αίμα', desc: 'Κάποια παυσίπονα όπως η ασπιρίνη αυξάνουν την αιμορραγία. Συμβουλέψου τον καλλιτέχνη σου πριν πάρεις κάτι.' },
              ].map((item, i) => (
                <div key={i} className="bg-gray-900 rounded-xl p-6 border border-pink-600/10">
                  <h3 className="text-white font-semibold mb-1">{item.title}</h3>
                  <p className="text-gray-400 text-sm leading-relaxed">{item.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Timing & hydration */}
        <section className="py-16 bg-gray-950">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid sm:grid-cols-2 gap-8">
              <div>
                <h2 className="text-2xl font-bold text-white mb-4">Πόσο νωρίτερα <span className="text-pink-500">να φας</span></h2>
                <p className="text-gray-300 leading-relaxed">
                  Ο ιδανικός χρόνος είναι περίπου μία με δύο ώρες πριν το ραντεβού. Για μεγάλα τατουάζ, έχε μαζί σου ένα σνακ για τα διαλείμματα, όπως μια μπάρα δημητριακών ή λίγους ξηρούς καρπούς.
                </p>
              </div>
              <div>
                <h2 className="text-2xl font-bold text-white mb-4">Η σημασία <span className="text-pink-500">της ενυδάτωσης</span></h2>
                <p className="text-gray-300 leading-relaxed">
                  Το καλά ενυδατωμένο δέρμα δουλεύεται πιο εύκολα. Πιες αρκετό νερό τις μέρες πριν, και έχε ένα μπουκάλι μαζί σου στη συνεδρία, ειδικά αν θα κρατήσει πολύ.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* What to bring */}
        <section className="py-16 bg-black">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="text-3xl font-bold text-white mb-6">Τι να έχεις μαζί σου <span className="text-pink-500">στη συνεδρία</span></h2>
            <div className="grid sm:grid-cols-2 gap-4">
              {[
                'Ένα μπουκάλι νερό για να μένεις ενυδατωμένος',
                'Ένα σνακ για τα διαλείμματα',
                'Κάτι γλυκό για ώρα ανάγκης',
                'Άνετα ρούχα που αφήνουν την περιοχή ελεύθερη',
              ].map((text, i) => (
                <div key={i} className="bg-gray-900 rounded-xl p-5 border border-pink-600/10 flex gap-3 items-start">
                  <span className="w-2 h-2 rounded-full bg-pink-500 shrink-0 mt-2" />
                  <p className="text-gray-300 text-sm leading-relaxed">{text}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* FAQ */}
        <section className="py-16 bg-gray-950">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="text-3xl font-bold text-white mb-8">Συχνές <span className="text-pink-500">Ερωτήσεις</span></h2>
            <div className="space-y-4">
              {faqSchema.mainEntity.map((item, i) => (
                <div key={i} className="bg-gray-900 rounded-xl p-6 border border-pink-600/10">
                  <h3 className="text-white font-semibold mb-2">{item.name}</h3>
                  <p className="text-gray-400 text-sm leading-relaxed">{item.acceptedAnswer.text}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* CTA */}
        <section className="py-20 bg-black">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="text-3xl font-bold text-white mb-6">Έτοιμος για το <span className="text-pink-500">επόμενο τατουάζ σου;</span></h2>
            <p className="text-gray-300 text-lg mb-8">
              Η ομάδα του 210 Tattoo Athens θα σου εξηγήσει πώς να προετοιμαστείς και τι να περιμένεις σε κάθε βήμα.
            </p>
            <a href="https://ig.me/m/210tattoo" target="_blank" rel="noopener noreferrer" className="bg-gradient-to-r from-pink-600 to-pink-700 hover:from-pink-700 hover:to-pink-800 px-8 py-4 rounded-lg text-lg font-semibold transition-all duration-300 inline-flex items-center gap-3 shadow-2xl hover:shadow-pink-500/30 transform hover:scale-105">
              <Instagram size={20} />
              Στείλε DM στο 210 Tattoo Athens
            </a>
          </div>
        </section>

        {/* Related articles */}
        <section className="py-16 bg-gray-950">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="text-2xl font-bold text-white mb-8">Σχετικά <span className="text-pink-500">Άρθρα</span></h2>
            <div className="grid sm:grid-cols-2 gap-4">
              <Link to="/blog/tattoo-pain-guide-athens" className="bg-gray-900 rounded-xl p-6 border border-pink-600/10 hover:border-pink-600/30 transition-colors duration-200 group">
                <p className="text-pink-500 text-xs uppercase tracking-widest mb-2">Άρθρο</p>
                <h3 className="text-white font-semibold group-hover:text-pink-400 transition-colors">Πονάει το τατουάζ; / Επώδυνα σημεία</h3>
                <p className="text-gray-400 text-sm mt-2 leading-relaxed">Χάρτης πόνου ανά σημείο του σώματος και πώς να τον διαχειριστείς.</p>
              </Link>
              <Link to="/blog/tattoo-pain-guide-athens" className="bg-gray-900 rounded-xl p-6 border border-pink-600/10 hover:border-pink-600/30 transition-colors duration-200 group">
                <p className="text-pink-500 text-xs uppercase tracking-widest mb-2">Άρθρο</p>
                <h3 className="text-white font-semibold group-hover:text-pink-400 transition-colors">Πονάει το Τατουάζ; Τα Πιο Επώδυνα Σημεία</h3>
                <p className="text-gray-400 text-sm mt-2 leading-relaxed">Τι να κάνεις πριν τη συνεδρία.</p>
              </Link>
            </div>
          </div>
        </section>

        <section className="py-16 bg-gray-950 border-t border-pink-600/10">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="text-2xl font-bold text-white mb-8 text-center">Υπηρεσίες <span className="text-pink-500">/ Services</span></h2>
            <div className="flex flex-wrap justify-center gap-4">
              <a href="/fine-line-tattoo-athens" className="border border-pink-600/20 hover:border-pink-500 text-gray-400 hover:text-white px-5 py-2 rounded-lg text-sm transition-colors duration-200">Fine Line Tattoo Athens</a>
              <a href="/realistic-tattoo-athens" className="border border-pink-600/20 hover:border-pink-500 text-gray-400 hover:text-white px-5 py-2 rounded-lg text-sm transition-colors duration-200">Realistic Tattoo Athens</a>
              <a href="/cover-up-tattoo-athens" className="border border-pink-600/20 hover:border-pink-500 text-gray-400 hover:text-white px-5 py-2 rounded-lg text-sm transition-colors duration-200">Cover Up Tattoo Athens</a>
              <a href="/black-gray-tattoo-athens" className="border border-pink-600/20 hover:border-pink-500 text-gray-400 hover:text-white px-5 py-2 rounded-lg text-sm transition-colors duration-200">Black &amp; Gray Tattoo Athens</a>
              <a href="/custom-tattoo-athens" className="border border-pink-600/20 hover:border-pink-500 text-gray-400 hover:text-white px-5 py-2 rounded-lg text-sm transition-colors duration-200">Custom Tattoo Athens</a>
            </div>
          </div>
        </section>
      </BlogLayout>
    </>
  );
}
